import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { QuizData, ElementType, QuizConfig } from '../types';
import QuizHeader from './QuizHeader';
import ResultPage from '../src/components/ResultPage';
import PageRenderer from './PageRenderer';
import { getUTMParameters, addUTMsToUrl } from '../src/lib/supabase';
import ScorePopup from '../src/components/ScorePopup';

// Default config to fallback to
const DEFAULT_CONFIG: QuizConfig = {
  colors: {
    primary: '#3b82f6',
    background: '#ffffff',
    text: '#1e293b',
    title: '#0f172a'
  },
  fontFamily: 'Inter',
  fontSizes: {
    title: 'text-2xl',
    subtitle: 'text-base',
    option: 'text-sm'
  },
  borderRadius: 'medium',
  contentWidth: 'medium',
  contentAlignment: 'center',
  containerStyle: 'flat',
  header: {
    showStepIndicator: true,
    showBackButton: true,
    progressBarStyle: 'default',
    logoPosition: 'center'
  },
  gamification: {
    coinsEnabled: false,
    style: 'coins',
    sound: 'default'
  },
  validation: {
    requiredMessage: 'Por favor, preencha este campo'
  },
  scripts: {
    head: '',
    footer: ''
  }
};

const LoadingSpinner: React.FC = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sky-500 border-t-transparent mb-2"></div>
    </div>
  </div>
);

const MobilePreviewModal: React.FC<{ quizData: QuizData; onClose: () => void }> = ({ quizData, onClose }) => {
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<number, string | string[]>>({});
  const [formValues, setFormValues] = React.useState<Record<number, Record<string, any>>>({});
  const [globalFormValues, setGlobalFormValues] = React.useState<Record<string, any>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentOverflow, setContentOverflow] = useState(false);
  const [coins, setCoins] = useState(0);
  const [confettiOn, setConfettiOn] = useState(false);
  const confettiTimeoutsRef = useRef<number[]>([]);
  const [confettiKey, setConfettiKey] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [coinAnimOn, setCoinAnimOn] = useState(false);
  const [coinAward, setCoinAward] = useState(0);
  const [coinKeyM, setCoinKeyM] = useState('coin-fly-m');

  // Função para redirecionamentos na preview mobile
  const handleMobilePreviewButtonAction = useCallback(async (action: 'next' | 'redirect', redirectUrl?: string, options?: { target?: '_self' | '_blank' }) => {
    if (action === 'redirect' && redirectUrl) {
      // Para preview, respeitar o target configurado
      const target = options?.target || '_blank';
      const redirectUrlWithUTMs = addUTMsToUrl(redirectUrl, getUTMParameters());

      if (target === '_blank') {
        // Para nova aba, não bloquear interface - só abrir e pronto
        window.open(redirectUrlWithUTMs, '_blank');
      } else {
        // Para mesma aba, bloquear interface durante o redirect
        // Nota: MobilePreviewModal não tem estado isRedirecting próprio
        window.location.href = redirectUrlWithUTMs;
      }
    }
  }, []);

  const [coinDxM, setCoinDxM] = useState(120);
  const [coinDyM, setCoinDyM] = useState(-240);
  const [plusLeftM, setPlusLeftM] = useState<number | null>(null);
  const [plusTopM, setPlusTopM] = useState<number | null>(null);
  const [plusKeyM, setPlusKeyM] = useState('plus-fly-m');
  const [plusDxM, setPlusDxM] = useState(0);
  const [plusDyM, setPlusDyM] = useState(0);
  const [coinStartLeftM, setCoinStartLeftM] = useState<number | null>(null);
  const [coinStartTopM, setCoinStartTopM] = useState<number | null>(null);

  const [awardedSteps, setAwardedSteps] = useState<Set<number>>(new Set());
  const [showScorePopup, setShowScorePopup] = useState(false);

  const currentPage = quizData.pages[currentPageIndex];
  const isLastPage = currentPageIndex === quizData.pages.length - 1;

  // Função helper para verificar se a página é de múltipla escolha
  const isMultipleChoice = (page: any) => {
    return page.elements?.some((element: any) =>
      element.type === ElementType.Options &&
      element.multiple === true
    ) || false;
  };

  // Debug: verificar tipo da página atual
  const currentPageType = isMultipleChoice(currentPage) ? 'MULTIPLA_ESCOLHA' : 'UNICA_ESCOLHA';

  useEffect(() => {
    const checkOverflow = () => {
      const el = contentRef.current;
      if (!el) return;
      const diff = Math.ceil(el.scrollHeight) - Math.ceil(el.clientHeight);
      setContentOverflow(diff > 0);
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [currentPageIndex]);

  // Desligar confetti e limpar timeouts quando mudar de página
  useEffect(() => {
    setConfettiOn(false);
    confettiTimeoutsRef.current.forEach(id => clearTimeout(id));
    confettiTimeoutsRef.current = [];

    // Reset validation state
    setInvalidFields([]);
    setValidationError(null);
  }, [currentPageIndex]);

  const [activeConfettiType, setActiveConfettiType] = React.useState<'exit' | 'entry'>('exit');

  // Função para disparar confetti
  const triggerConfetti = (type: 'exit' | 'entry' = 'exit') => {
    setActiveConfettiType(type);
    setConfettiKey(prev => prev + 1);
    setConfettiOn(true);
  };

  // Trigger confetti on entry if configured
  useEffect(() => {
    if (currentPage?.confettiOnEntryConfig?.enabled) {
      // Pequeno delay para garantir que a página renderizou
      const t = setTimeout(() => triggerConfetti('entry'), 100);
      return () => clearTimeout(t);
    }
  }, [currentPageIndex]);

  // Memoizar partículas de confetti para evitar recálculo a cada render
  const confettiParticles = React.useMemo(() => {
    if (!confettiOn) return null;

    const confettiConfig = activeConfettiType === 'entry'
      ? currentPage.confettiOnEntryConfig
      : currentPage.confettiConfig;
    const amountBase = confettiConfig?.amount === 'high' ? 100 : confettiConfig?.amount === 'low' ? 30 : 60;
    const direction = confettiConfig?.direction || 'center';
    const amount = direction === 'sides' ? amountBase * 2.5 : amountBase;
    const style = confettiConfig?.style || 'paper';

    const particles = Array.from({ length: amount }).map((_, i) => {
      let startX = '50%';
      let startY = '50%';
      let angle, dist;

      if (direction === 'top') {
        startX = `${Math.random() * 100}%`;
        startY = '-10%';
        angle = Math.PI / 2 + (Math.random() - 0.5) * 0.5;
        dist = window.innerHeight + 100;
      } else if (direction === 'sides') {
        const side = i % 2 === 0 ? 0 : 100;
        startX = `${side}%`;
        startY = `${60 + Math.random() * 30}%`; // Start from lower sides (60-90%)

        // Angle: Sweep towards center
        if (side === 0) {
          // Left side: Aim between -10 and -80 degrees
          angle = -0.2 - (Math.random() * 1.2);
        } else {
          // Right side: Aim between -170 and -100 degrees
          angle = -Math.PI + 0.2 + (Math.random() * 1.2);
        }

        dist = confettiConfig?.amount === 'high' ? 600 + Math.random() * 400 : 400 + Math.random() * 300;
      } else {
        // Center (default)
        angle = Math.random() * Math.PI * 2;
        // Aumentar distância para 'high'
        dist = confettiConfig?.amount === 'high' ? 200 + Math.random() * 350 : 110 + Math.random() * 200;
      }

      // Cores específicas por estilo
      let colors;
      if (style === 'stars') {
        colors = ['#FFD700', '#FFC107', '#FFEB3B', '#F9A825', '#FFB300'];
      } else if (style === 'fireworks') {
        colors = ['#FF1744', '#F50057', '#D500F9', '#651FFF', '#00E5FF', '#00E676', '#FFD600', '#FF6D00'];
      } else {
        colors = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6'];
      }

      const baseSize = style === 'stars' ? 16 : style === 'fireworks' ? 4 : 5;
      const sizeVariation = style === 'stars' ? 14 : style === 'fireworks' ? 3 : 5;

      const baseDuration = direction === 'sides' ? 2500 : (style === 'fireworks' ? 1000 : 700);
      const varDuration = direction === 'sides' ? 1000 : 500;

      return {
        startX,
        startY,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        duration: baseDuration + Math.random() * varDuration,
        size: baseSize + Math.random() * sizeVariation,
        delay: Math.random() * 60,
        bg: colors[i % colors.length],
        startRot: Math.floor(Math.random() * 360),
        endRot: Math.floor(Math.random() * 360)
      };
    });

    return { particles, style };
  }, [confettiKey, currentPage, activeConfettiType]);

  const handleAnswer = (answer: string | string[]) => {
    const wasAnswered = answers[currentPage.id] !== undefined;
    setAnswers(prev => ({ ...prev, [currentPage.id]: answer }));

    // Para perguntas de escolha única, avançar automaticamente após feedback visual
    if (!isMultipleChoice(currentPage) && !isLastPage) {
      const gm = quizData.config.gamification || {};
      const hdr = quizData.config.header || {};
      const onCover = hdr.hideOnFirstPage === true && currentPageIndex === 0;
      const pointsToAward = currentPage.scoreConfig?.enabled ? (currentPage.scoreConfig.points || 0) : 0;

      if (gm.coinsEnabled && !wasAnswered && !onCover && !awardedSteps.has(currentPageIndex) && pointsToAward > 0) {
        playCoin();

        // Calcular coordenadas para animação
        const t = document.querySelector('[data-coin-target="true"][data-coin-context="mobile"]');
        if (t) {
          const r = t.getBoundingClientRect();
          // Centro da tela do mobile (aproximado, pois é um modal)
          // Melhor usar o centro do container do modal se possível, mas window serve como fallback
          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;

          // Ajuste fino para o modal mobile
          // O modal está centralizado, então o centro da tela é o centro do modal
          const targetX = r.left + r.width / 2;
          const targetY = r.top + r.height / 2;

          const dx = targetX - centerX;
          const dy = targetY - centerY;

          setCoinDxM(dx);
          setCoinDyM(dy);
          setCoinKeyM(prev => prev + 'x'); // Force re-render
        }

        setCoinAward(pointsToAward);
        setCoinAnimOn(true);
        setAwardedSteps(prev => new Set(prev).add(currentPageIndex));

        if (currentPage.scorePopupConfig?.enabled) {
          setShowScorePopup(true);
        }

        setTimeout(() => setCoins(c => c + pointsToAward), 950);
        setTimeout(() => setCoinAnimOn(false), 1100);
      }

      const hasConfetti = currentPage.confettiConfig?.enabled;
      if (hasConfetti) {
        triggerConfetti();
      }

      const delay = hasConfetti ? 1500 : 500;
      setTimeout(() => {
        setCurrentPageIndex(prev => prev + 1);
        // Reset scroll
        if (contentRef.current) contentRef.current.scrollTop = 0;
      }, delay);
    } else {
      // Se for última página ou múltipla escolha, apenas atualiza estado
      // O botão "Próximo" ou "Finalizar" cuidará do resto
    }
  };

  const handleNext = () => {
    // Validar campos obrigatórios se houver formulário
    const hasRequiredForm = currentPage.elements?.some(
      (el: any) => el.type === ElementType.Form && el.required === true
    );

    if (hasRequiredForm) {
      const currentFormValues = formValues[currentPage.id] || {};
      const invalidIds: string[] = [];

      currentPage.elements?.forEach((el: any) => {
        if (el.type !== ElementType.Form) return;
        if (el.required !== true) return;

        const key = el.fieldId || String(el.id);
        const val = currentFormValues[key];

        if (!val || (typeof val === 'string' && val.trim() === '') || (typeof val === 'object' && val.value === '')) {
          invalidIds.push(key);
        }
      });

      if (invalidIds.length > 0) {
        setInvalidFields(invalidIds);
        setValidationError(quizData.config.validation?.requiredMessage || 'Preencha os campos obrigatórios');
        return;
      }
    }

    if (isLastPage) {
      // Finalizar quiz
      onClose();
    } else {
      // Gamification logic for "Next" button (Multi-choice or Form)
      const gm = quizData.config.gamification || {};
      const hdr = quizData.config.header || {};
      const onCover = hdr.hideOnFirstPage === true && currentPageIndex === 0;
      const pointsToAward = currentPage.scoreConfig?.enabled ? (currentPage.scoreConfig.points || 0) : 0;
      const wasAnswered = awardedSteps.has(currentPageIndex); // Check if already awarded for this step

      if (gm.coinsEnabled && !wasAnswered && !onCover && pointsToAward > 0) {
        playCoin();

        const t = document.querySelector('[data-coin-target="true"][data-coin-context="mobile"]');
        if (t) {
          const r = t.getBoundingClientRect();
          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;
          const targetX = r.left + r.width / 2;
          const targetY = r.top + r.height / 2;
          const dx = targetX - centerX;
          const dy = targetY - centerY;
          setCoinDxM(dx);
          setCoinDyM(dy);
          setCoinKeyM(prev => prev + 'x');
        }

        setCoinAward(pointsToAward);
        setCoinAnimOn(true);
        setAwardedSteps(prev => new Set(prev).add(currentPageIndex));

        if (currentPage.scorePopupConfig?.enabled) {
          setShowScorePopup(true);
        }

        setTimeout(() => setCoins(c => c + pointsToAward), 950);
        setTimeout(() => setCoinAnimOn(false), 1100);
      }

      const hasConfetti = currentPage.confettiConfig?.enabled;
      if (hasConfetti) {
        triggerConfetti();
        setTimeout(() => {
          setCurrentPageIndex(prev => prev + 1);
          if (contentRef.current) contentRef.current.scrollTop = 0;
        }, 500);
      } else {
        setCurrentPageIndex(prev => prev + 1);
        if (contentRef.current) contentRef.current.scrollTop = 0;
      }
    }
  };

  const handlePrev = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(prev => prev - 1);
    }
  };

  const handleFormChange = (values: Record<string, any>) => {
    setFormValues(prev => ({
      ...prev,
      [currentPage.id]: values
    }));
    setGlobalFormValues(prev => ({
      ...prev,
      ...values
    }));

    // Limpar erro de validação quando usuário digita
    if (validationError) {
      setValidationError(null);
      setInvalidFields([]);
    }
  };

  const playCoin = () => {
    const soundType = quizData.config.gamification?.sound || 'default';
    if (soundType === 'default') {
      try {
        const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(700, ctx.currentTime);
        g.gain.setValueAtTime(0.001, ctx.currentTime);
        o.connect(g);
        g.connect(ctx.destination);
        o.start();
        o.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
        o.stop(ctx.currentTime + 0.3);
      } catch { }
    } else {
      const audio = new Audio(`/sounds/${soundType}.mp3`);
      audio.volume = 0.6;
      audio.play().catch(e => console.warn('⚠️ [Mobile] Erro ao tocar som:', e));
    }
  };

  // Configuração combinada
  const config = useMemo(() => {
    const loadedConfig = quizData.config;
    return {
      ...DEFAULT_CONFIG,
      ...loadedConfig,
      colors: { ...DEFAULT_CONFIG.colors, ...loadedConfig.colors },
      fontSizes: { ...DEFAULT_CONFIG.fontSizes, ...loadedConfig.fontSizes },
      header: { ...DEFAULT_CONFIG.header, ...loadedConfig.header },
      gamification: { ...DEFAULT_CONFIG.gamification, ...loadedConfig.gamification },
      validation: { ...DEFAULT_CONFIG.validation, ...loadedConfig.validation },
      scripts: { ...DEFAULT_CONFIG.scripts, ...loadedConfig.scripts },
    } as QuizConfig;
  }, [quizData]);

  const currentAnswer = answers[currentPage.id];
  const selectedOption = (currentAnswer && typeof currentAnswer === 'object' && 'value' in currentAnswer)
    ? (currentAnswer as any).value
    : currentAnswer;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-[375px] h-[80vh] max-h-[812px] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">

        {/* Header */}
        <div className="pt-4 px-4 pb-2 bg-white border-b border-slate-100">
          <QuizHeader
            currentStep={currentPageIndex}
            totalSteps={quizData.pages.length}
            config={config}
            onBack={currentPageIndex > 0 ? handlePrev : undefined}
            coins={coins}
            coinContext="mobile"
          />
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          className={`flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 ${config.contentAlignment === 'center' ? 'flex flex-col justify-center' : ''
            }`}
          style={{
            backgroundColor: config.colors.background,
            fontFamily: config.fontFamily
          }}
        >
          <PageRenderer
            key={currentPageIndex}
            page={currentPage}
            config={config}
            onOptionSelect={handleAnswer}
            selectedOption={selectedOption}
            multiple={isMultipleChoice(currentPage)}
            onNext={handleNext}
            onFormChange={handleFormChange}
            formValues={formValues[currentPage.id] || {}}
            invalidFieldIds={invalidFields}
            validationMessage={validationError || undefined}
            onButtonAction={handleMobilePreviewButtonAction}
            isRedirecting={false}
            priceCheckboxes={{}}
            onPriceCheckboxChange={() => { }}
            coins={coins}
            onConfetti={() => triggerConfetti('exit')}
            previewMode={true}
            globalFormValues={globalFormValues}
          />
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-slate-100 rounded-full text-slate-500 hover:bg-slate-200 transition-colors z-50"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Confetti Layer */}
        {confettiOn && confettiParticles && (
          <div className="pointer-events-none absolute inset-0 z-40 overflow-hidden rounded-xl">
            {(() => {
              const { particles, style } = confettiParticles;
              const borderRadius = style === 'circles' || style === 'fireworks' ? '50%' : style === 'stars' ? '0' : '2px';
              const clipPath = style === 'stars' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' : undefined;

              return (
                <>
                  {particles.map((p, i) => (
                    <span
                      key={i}
                      className="absolute"
                      style={{
                        left: p.startX,
                        top: p.startY,
                        width: `${p.size}px`,
                        height: `${p.size * (style === 'stars' ? 1 : 1.4)}px`,
                        backgroundColor: p.bg,
                        borderRadius: borderRadius,
                        clipPath: clipPath,
                        transform: 'translate(-50%, -50%)',
                        animation: `burst-m-${i} ${p.duration}ms ease-out ${p.delay}ms forwards`
                      }}
                    />
                  ))}
                  <style>{`
                    ${particles.map((p, i) =>
                    `@keyframes burst-m-${i}{0%{transform:translate(-50%,-50%) scale(0.6) rotate(${p.startRot}deg);opacity:1}50%{opacity:1}100%{transform:translate(calc(-50% + ${p.dx}px), calc(-50% + ${p.dy}px)) rotate(${p.endRot}deg) scale(1);opacity:0}}`
                  ).join('')}
                  `}</style>
                </>
              );
            })()}
          </div>
        )}

        {/* Coin Animation Layer */}
        {coinAnimOn && (
          <div className="pointer-events-none absolute inset-0 z-[60] flex items-center justify-center overflow-hidden rounded-xl">
            {config.gamification?.animationType === 'simple' ? (
              <div
                key={coinKeyM}
                className="absolute"
                style={{
                  left: `calc(50% + ${coinDxM}px)`,
                  top: `calc(50% + ${coinDyM}px)`,
                  transform: 'translate(-50%, -50%)',
                  animation: 'coin-pop-m-simple 1s ease-out forwards'
                }}
              >
                <div
                  className="text-2xl font-bold"
                  style={{
                    color: config.gamification?.style === 'money' ? '#15803d' : '#b45309',
                    textShadow: '0 1px 0 #fff'
                  }}
                >
                  +{coinAward}
                </div>
              </div>
            ) : (
              <div
                key={coinKeyM}
                className="relative flex items-center justify-center"
                style={{
                  animation: 'coin-fly-m 1s ease-in-out forwards'
                }}
              >
                <div className="text-4xl filter drop-shadow-lg">
                  {config.gamification?.style === 'money' ? '💵' : '🪙'}
                </div>
                <div
                  className="absolute top-0 right-0 -mt-2 -mr-2 text-xl font-bold"
                  style={{
                    color: config.gamification?.style === 'money' ? '#15803d' : '#b45309',
                    textShadow: '0 1px 0 #fff'
                  }}
                >
                  +{coinAward}
                </div>
              </div>
            )}
            <style>{`
              @keyframes coin-fly-m {
                0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
                20% { transform: translate(0, 0) scale(1.2); opacity: 1; }
                40% { transform: translate(0, 0) scale(1); opacity: 1; }
                80% { transform: translate(${coinDxM}px, ${coinDyM}px) scale(0.5); opacity: 1; }
                100% { transform: translate(${coinDxM}px, ${coinDyM}px) scale(0); opacity: 0; }
              }
              @keyframes coin-pop-m-simple {
                0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
                20% { transform: translate(-50%, -50%) scale(1.5); opacity: 1; }
                80% { transform: translate(-50%, -150%) scale(1); opacity: 1; }
                100% { transform: translate(-50%, -200%) scale(0.5); opacity: 0; }
              }
            `}</style>
          </div>
        )}

        {/* Score Popup */}
        {showScorePopup && (
          <div className="absolute inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
            <div className="pointer-events-auto">
              <ScorePopup
                points={coinAward}
                text={currentPage.scorePopupConfig?.text || 'Foi adicionado ao seu saldo!'}
                footer={currentPage.scorePopupConfig?.footer || 'Continue para ganhar mais! 🚀'}
                onClose={() => setShowScorePopup(false)}
                style={config.gamification?.style}
                preText={config.gamification?.style === 'money' ? 'US$ ' : ''}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobilePreviewModal;